/**
 * MySurveys Component
 * Displays list of assigned surveys for participants
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import NotificationCenter from './NotificationCenter';
import RoleToggle from './RoleToggle';
import { getMCSurveys, checkIsMCAdmin } from '../../services/mcAPI';
import './MySurveys.css';

const MySurveys = () => {
  const navigate = useNavigate();
  const { user, getAccessToken, logout } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [gpsAssignments, setGpsAssignments] = useState([]);
  const [mcSurveys, setMcSurveys] = useState([]);
  const [isAnyAdmin, setIsAnyAdmin] = useState(false);
  const [adminCheckLoading, setAdminCheckLoading] = useState(true);

  useEffect(() => {
    // Only load surveys when user is available
    if (user && user.email) {
      console.log('User is ready, loading surveys for:', user.email);
      loadAllSurveys();
      checkAdminStatus();
    } else {
      console.log('Waiting for user to be available...', { user });
    }
  }, [user]);

  const checkAdminStatus = async () => {
    try {
      setAdminCheckLoading(true);
      const accessToken = await getAccessToken();
      const leadingApiUrl = process.env.REACT_APP_API_URL || 'https://nh9pshlcw7.execute-api.us-west-2.amazonaws.com/prod';
      
      // Check both admin groups in parallel
      const [leadingRes, mcRes] = await Promise.allSettled([
        fetch(`${leadingApiUrl}/leading/api/users/me/is-admin`, {
          headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' }
        }).then(r => r.json()),
        checkIsMCAdmin(accessToken)
      ]);

      const isLeadingAdmin = leadingRes.status === 'fulfilled' && (leadingRes.value?.data?.isAdmin || false);
      const isMCAdminResult = mcRes.status === 'fulfilled' && (mcRes.value?.isMCAdmin || false);
      
      console.log('Admin check results:', { isLeadingAdmin, isMCAdmin: isMCAdminResult });
      setIsAnyAdmin(isLeadingAdmin || isMCAdminResult);
    } catch (err) {
      console.error('Error checking admin status:', err);
      setIsAnyAdmin(false);
    } finally {
      setAdminCheckLoading(false);
    }
  };

  const loadAllSurveys = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('=== LOADING ALL SURVEYS ===');
      console.log('User object:', user);
      
      const accessToken = await getAccessToken();
      const leadingApiUrl = process.env.REACT_APP_API_URL || 'https://nh9pshlcw7.execute-api.us-west-2.amazonaws.com/prod';
      const gpsApiUrl = process.env.REACT_APP_GPS_API_URL || 'https://jmaffrrt2j.execute-api.us-west-2.amazonaws.com/prod';
      
      console.log('API URLs:', { leadingApiUrl, gpsApiUrl });
      
      // Get user email - use it as-is from EntraID
      const userEmail = user?.email || user?.preferred_username;
      
      console.log('User email:', userEmail);
      
      const leadingUrl = `${leadingApiUrl}/leading/api/surveys/my-surveys`;
      const gpsUrl = `${gpsApiUrl}/api/team-member/assignments?email=${encodeURIComponent(userEmail)}`;
      
      console.log('Fetching from URLs:', { leadingUrl, gpsUrl });
      
      // Load Leading at Gallo surveys, GPS assignments, and MC surveys in parallel
      const [leadingSurveysResponse, gpsAssignmentsResponse, mcSurveysResponse] = await Promise.allSettled([
        fetch(leadingUrl, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(gpsUrl, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }),
        getMCSurveys(userEmail)
      ]);

      console.log('Response statuses:', {
        leading: leadingSurveysResponse.status,
        gps: gpsAssignmentsResponse.status
      });

      // Process Leading at Gallo surveys
      if (leadingSurveysResponse.status === 'fulfilled' && leadingSurveysResponse.value.ok) {
        const data = await leadingSurveysResponse.value.json();
        console.log('Leading surveys data:', data);
        const surveysArray = data.data?.surveys || data.surveys || [];
        console.log('Setting surveys:', surveysArray.length, 'surveys');
        setSurveys(surveysArray);
      } else {
        console.warn('Failed to load Leading at Gallo surveys:', {
          status: leadingSurveysResponse.status,
          statusCode: leadingSurveysResponse.value?.status,
          statusText: leadingSurveysResponse.value?.statusText
        });
        if (leadingSurveysResponse.status === 'fulfilled') {
          const errorText = await leadingSurveysResponse.value.text();
          console.error('Leading API error response:', errorText);
        }
      }

      // Process GPS assignments
      if (gpsAssignmentsResponse.status === 'fulfilled' && gpsAssignmentsResponse.value.ok) {
        const responseText = await gpsAssignmentsResponse.value.text();
        console.log('GPS assignments raw response:', responseText);
        try {
          const data = JSON.parse(responseText);
          console.log('GPS assignments parsed data:', data);
          const assignmentsArray = data.assignments || [];
          console.log('Setting GPS assignments:', assignmentsArray.length, 'assignments');
          setGpsAssignments(assignmentsArray);
        } catch (parseError) {
          console.error('Failed to parse GPS response:', parseError);
          console.error('Response text:', responseText);
        }
      } else {
        console.warn('Failed to load GPS assignments:', {
          status: gpsAssignmentsResponse.status,
          statusCode: gpsAssignmentsResponse.value?.status,
          statusText: gpsAssignmentsResponse.value?.statusText
        });
        if (gpsAssignmentsResponse.status === 'fulfilled') {
          const errorText = await gpsAssignmentsResponse.value.text();
          console.error('GPS API error response:', errorText);
        }
      }

      // Process Meaningful Conversations surveys
      if (mcSurveysResponse.status === 'fulfilled') {
        console.log('MC surveys data:', mcSurveysResponse.value);
        setMcSurveys(mcSurveysResponse.value?.surveys || []);
      } else {
        console.warn('Failed to load MC surveys:', mcSurveysResponse.reason?.message);
      }
      
    } catch (err) {
      console.error('Error loading surveys:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'PENDING': { label: 'Pending', className: 'status-pending' },
      'IN_PROGRESS': { label: 'In Progress', className: 'status-in-progress' },
      'COMPLETED': { label: 'Completed', className: 'status-completed' },
      'EXPIRED': { label: 'Expired', className: 'status-expired' }
    };
    return badges[status] || badges['PENDING'];
  };

  const getDaysRemaining = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffTime = due - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Overdue';
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return '1 day remaining';
    return `${diffDays} days remaining`;
  };

  const handleSurveyClick = (survey) => {
    if (survey.status === 'COMPLETED' || survey.status === 'EXPIRED') {
      return;
    }
    navigate(`/leading/survey/${survey.access_token}`);
  };

  const getButtonText = (status) => {
    if (status === 'PENDING') return 'Start Survey';
    if (status === 'IN_PROGRESS') return 'Continue Survey';
    if (status === 'COMPLETED') return 'Completed';
    if (status === 'EXPIRED') return 'Expired';
    return 'View Survey';
  };

  // Log render state
  console.log('=== RENDER STATE ===', {
    loading,
    error,
    surveysCount: surveys.length,
    gpsAssignmentsCount: gpsAssignments.length,
    surveys: surveys.map(s => ({ id: s.id, name: s.survey_name })),
    gpsAssignments: gpsAssignments.map(a => ({ id: a.assignmentId, name: a.assessmentName }))
  });

  return (
    <div className="my-surveys-container">
      <RoleToggle />
      {/* Header */}
      <div className="my-surveys-header">
        <div className="container">
          <div className="header-content">
            <div className="header-left">
              <img 
                src="/gallo-logo.png" 
                alt="Gallo" 
                className="header-logo"
              />
            </div>
            <h1 className="page-title">My Surveys</h1>
            <div className="header-actions">
              <span className="header-greeting">
                Hi {(() => {
                  const email = user?.email || '';
                  const firstName = email.split('@')[0].split('.')[0];
                  return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
                })()}!
              </span>
              <NotificationCenter />
              <button 
                onClick={handleLogout}
                className="questionnaire-button sign-out-button"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="my-surveys-content">
        <div className="container">
          {/* Loading State */}
          {loading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading your surveys...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="error-state">
              <div className="error-icon">⚠️</div>
              <h3>Unable to Load Surveys</h3>
              <p>{error}</p>
              <button 
                onClick={loadAllSurveys}
                className="questionnaire-button"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Empty State - Simple Message */}
          {(() => {
            const hasLeadingSurveys = surveys.filter(s => s.status !== 'COMPLETED').length > 0;
            const hasGPSPillars = gpsAssignments.length > 0 && gpsAssignments.some(assignment => 
              assignment.pillars?.some(pillar => pillar.isActive && pillar.status !== 'COMPLETED')
            );
            const hasMCSurveys = mcSurveys.filter(s => s.status !== 'COMPLETED').length > 0;
            const shouldShowEmpty = !loading && !error && !hasLeadingSurveys && !hasGPSPillars && !hasMCSurveys;
            
            console.log('Empty State Check:', {
              loading,
              error,
              surveysTotal: surveys.length,
              hasLeadingSurveys,
              gpsAssignmentsTotal: gpsAssignments.length,
              hasGPSPillars,
              shouldShowEmpty
            });
            
            return shouldShowEmpty ? (
              <div className="empty-state">
                <p className="empty-state-message">
                  You don't have any surveys due at this time
                </p>
              </div>
            ) : null;
          })()}

          {/* GPS Pillars Section - Show individual pillars as survey cards */}
          {!loading && !error && gpsAssignments.length > 0 && gpsAssignments.some(assignment => 
            assignment.pillars?.some(pillar => pillar.isActive && pillar.status !== 'COMPLETED')
          ) && (
            <div className="surveys-section">
              <h2 className="section-title">
                <span className="section-title-text">GPS Operational Excellence</span>
              </h2>
              <div className="surveys-grid">
                {gpsAssignments.flatMap(assignment => 
                  assignment.pillars
                    ?.filter(pillar => pillar.isActive && pillar.status !== 'COMPLETED') // Only show active, non-completed pillars
                    ?.map(pillar => {
                    const isCompleted = pillar.status === 'COMPLETED';
                    const isActive = pillar.isActive;
                    const isPending = pillar.status === 'PENDING' || pillar.status === 'NOT_STARTED';
                    const isActionable = isActive && !isCompleted;
                    
                    // Calculate days remaining until pillar closes
                    let daysRemaining = null;
                    let daysRemainingText = '';
                    if (isActive && pillar.activationDate) {
                      const now = new Date();
                      // Pillar is active for 7 days from activation
                      const activationDate = new Date(pillar.activationDate);
                      const closeDate = new Date(activationDate);
                      closeDate.setDate(closeDate.getDate() + 7);
                      
                      const diffTime = closeDate - now;
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                      
                      daysRemaining = diffDays;
                      if (diffDays < 0) {
                        daysRemainingText = 'Overdue';
                      } else if (diffDays === 0) {
                        daysRemainingText = 'Due today';
                      } else if (diffDays === 1) {
                        daysRemainingText = '1 day remaining';
                      } else {
                        daysRemainingText = `${diffDays} days remaining`;
                      }
                    }
                    
                    return (
                      <div 
                        key={`${assignment.assignmentId}-${pillar.pillarNumber}`}
                        className={`survey-card gps-card ${isActionable ? 'actionable' : 'inactive'}`}
                      >
                        {/* Header with GPS Logo */}
                        <div className="survey-card-header gps-logo-header" style={{ 
                          textAlign: 'center', 
                          padding: '2rem 1rem 1.5rem',
                          background: '#ffffff',
                          borderBottom: '1px solid #e5e5e5'
                        }}>
                          <img 
                            src="/gps_no_words1.png" 
                            alt="GPS" 
                            style={{ height: '150px', marginBottom: '0', display: 'block', margin: '0 auto' }} 
                          />
                        </div>

                        {/* Body with Survey Info */}
                        <div className="survey-card-body" style={{ padding: '1.5rem' }}>
                          <div className="survey-info">
                            <div className="info-item" style={{ justifyContent: 'flex-start', gap: '0.5rem' }}>
                              <span className="info-label">Pillar Name:</span>
                              <span className="info-value" style={{ fontWeight: '600' }}>{pillar.pillarName}</span>
                            </div>
                            
                            <div className="info-item" style={{ justifyContent: 'flex-start', gap: '0.5rem' }}>
                              <span className="info-label">Type:</span>
                              <span className="info-value">GPS Assessment</span>
                            </div>
                            
                            <div className="info-item" style={{ justifyContent: 'flex-start', gap: '0.5rem' }}>
                              <span className="info-label">Status:</span>
                              <span className={`status-badge ${
                                isCompleted ? 'status-completed' : 
                                isActive ? 'status-in-progress' : 
                                'status-pending'
                              }`} style={{ 
                                display: 'inline-block'
                              }}>
                                {isCompleted ? 'Completed' : isActive ? 'Active' : 'Pending'}
                              </span>
                            </div>
                            
                            {isActive && daysRemainingText && (
                              <div className="info-item" style={{ marginTop: '0.75rem' }}>
                                <span className={`days-remaining ${daysRemaining < 2 ? 'overdue' : ''}`} style={{
                                  fontSize: '0.9375rem',
                                  fontWeight: '600'
                                }}>
                                  ⏰ {daysRemainingText}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Footer with Action Button */}
                        <div className="survey-card-footer" style={{ padding: '1rem 1.5rem 1.5rem' }}>
                          <button
                            onClick={() => {
                              if (isActionable) {
                                // Navigate directly to the pillar questionnaire
                                navigate(`/gps/pillar/${assignment.assignmentId}/${pillar.pillarNumber}`);
                              }
                            }}
                            disabled={!isActionable}
                            className={`questionnaire-button ${isActionable ? 'questionnaire-button-primary' : ''}`}
                            style={{ 
                              width: '100%',
                              fontSize: '1.125rem',
                              fontWeight: '600',
                              textAlign: 'center',
                              padding: '0.875rem 1.5rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {isCompleted ? 'Completed' : isActive ? 'Start Survey' : 'Not Available Yet'}
                          </button>
                        </div>
                      </div>
                    );
                  }) || []
                )}
              </div>
            </div>
          )}

          {/* Leading at Gallo Surveys Section */}
          {!loading && !error && surveys.filter(s => s.status !== 'COMPLETED').length > 0 && (
            <div className="surveys-section">
              <h2 className="section-title">
                <span className="section-title-text">Leading at Gallo</span>
              </h2>
              <div className="surveys-grid">
                {surveys.filter(survey => survey.status !== 'COMPLETED').map(survey => {
                  const statusBadge = getStatusBadge(survey.status);
                  const daysRemaining = getDaysRemaining(survey.due_date);
                  const isActionable = survey.status === 'PENDING' || survey.status === 'IN_PROGRESS';
                  
                  // Parse survey name to extract program level and type
                  const parseSurveyInfo = () => {
                    const stageKeywords = ['BASELINE', 'MIDPOINT', 'FINAL', 'FOLLOWUP'];
                    let foundStage = '';
                    let programLevel = '';
                    
                    for (const keyword of stageKeywords) {
                      const regex = new RegExp(`\\s*-?\\s*${keyword}\\s*-?\\s*`, 'i');
                      const match = survey.survey_name.match(regex);
                      if (match) {
                        foundStage = keyword.charAt(0) + keyword.slice(1).toLowerCase(); // Title case
                        // Extract program level (everything after stage keyword)
                        const afterStage = survey.survey_name.substring(match.index).replace(/^\s*-?\s*[A-Z]+\s*-?\s*/, '');
                        programLevel = afterStage.trim();
                        break;
                      }
                    }
                    
                    return { programLevel: programLevel || survey.survey_name, stage: foundStage || 'Survey' };
                  };
                  
                  const { programLevel, stage } = parseSurveyInfo();
                  
                  return (
                    <div 
                          key={survey.id} 
                          className={`survey-card ${isActionable ? 'actionable' : 'inactive'}`}
                        >
                          {/* Header with Gallo Logo */}
                          <div className="survey-card-header" style={{ 
                            textAlign: 'center', 
                            padding: '2rem 1rem 1.5rem',
                            background: '#ffffff',
                            borderBottom: '1px solid #e5e5e5'
                          }}>
                            <img 
                              src="/gallou.png" 
                              alt="Gallo" 
                              style={{ height: '80px', marginBottom: '0', display: 'block', margin: '0 auto' }} 
                            />
                          </div>

                          {/* Body with Survey Info */}
                          <div className="survey-card-body" style={{ padding: '1.5rem' }}>
                            <div className="survey-info">
                              <div className="info-item" style={{ justifyContent: 'flex-start', gap: '0.5rem' }}>
                                <span className="info-label">Program Level:</span>
                                <span className="info-value" style={{ fontWeight: '600' }}>{programLevel}</span>
                              </div>
                              
                              <div className="info-item" style={{ justifyContent: 'flex-start', gap: '0.5rem' }}>
                                <span className="info-label">Type:</span>
                                <span className="info-value">{stage}</span>
                              </div>
                              
                              <div className="info-item" style={{ justifyContent: 'flex-start', gap: '0.5rem' }}>
                                <span className="info-label">For:</span>
                                <span className="info-value">
                                  {survey.respondent_type === 'SELF' ? 'Self Assessment' : survey.participant_name}
                                </span>
                              </div>
                              
                              <div className="info-item" style={{ justifyContent: 'flex-start', gap: '0.5rem' }}>
                                <span className="info-label">Due Date:</span>
                                <span className="info-value">
                                  {new Date(survey.due_date).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </span>
                              </div>
                              
                              <div className="info-item" style={{ justifyContent: 'flex-start', gap: '0.5rem' }}>
                                <span className="info-label">Status:</span>
                                <span className={`status-badge ${statusBadge.className}`} style={{ 
                                  display: 'inline-block'
                                }}>
                                  {statusBadge.label}
                                </span>
                              </div>
                              
                              {isActionable && (
                                <div className="info-item" style={{ marginTop: '0.75rem' }}>
                                  <span className={`days-remaining ${daysRemaining.includes('Overdue') ? 'overdue' : ''}`} style={{
                                    fontSize: '0.9375rem',
                                    fontWeight: '600'
                                  }}>
                                    {daysRemaining}
                                  </span>
                                </div>
                              )}
                              
                              {survey.status === 'COMPLETED' && survey.completed_at && (
                                <div className="info-item" style={{ marginTop: '0.75rem' }}>
                                  <span className="info-label">Completed:</span>
                                  <span className="info-value">
                                    {new Date(survey.completed_at).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Footer with Action Button */}
                          <div className="survey-card-footer" style={{ padding: '1rem 1.5rem 1.5rem' }}>
                            <button
                              onClick={() => handleSurveyClick(survey)}
                              disabled={!isActionable}
                              className={`questionnaire-button ${isActionable ? 'questionnaire-button-primary' : ''}`}
                              style={{ 
                                width: '100%',
                                fontSize: '1.125rem',
                                fontWeight: '600',
                                textAlign: 'center',
                                padding: '0.875rem 1.5rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              {getButtonText(survey.status)}
                            </button>
                          </div>
                        </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Meaningful Conversations Surveys Section */}
          {!loading && !error && mcSurveys.filter(s => s.status !== 'COMPLETED').length > 0 && (
            <div className="surveys-section">
              <h2 className="section-title">
                <span className="section-title-text">Meaningful Conversations</span>
              </h2>
              <div className="surveys-grid">
                {mcSurveys.filter(s => s.status !== 'COMPLETED').map(mcSurvey => {
                  const isActionable = mcSurvey.status === 'PENDING' || mcSurvey.status === 'SENT';
                  const isCompleted = mcSurvey.status === 'COMPLETED';
                  const now = new Date();
                  const endDate = mcSurvey.cycle_end_date 
                    ? (() => { const d = new Date(mcSurvey.cycle_end_date); return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()); })()
                    : new Date(mcSurvey.expires_at);
                  const isExpired = now > endDate;
                  const diffDays = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
                  const daysText = diffDays < 0 ? 'Overdue' : diffDays === 0 ? 'Due today' : diffDays === 1 ? '1 day remaining' : `${diffDays} days remaining`;

                  const getMCButtonText = () => {
                    if (isCompleted) return 'Completed';
                    if (isExpired) return 'Expired';
                    return 'Start Survey';
                  };

                  return (
                    <div
                      key={mcSurvey.id}
                      className={`survey-card ${isActionable && !isExpired ? 'actionable' : 'inactive'}`}
                    >
                      {/* Header with Gallo Logo */}
                      <div className="survey-card-header" style={{
                        textAlign: 'center',
                        padding: '2rem 1rem 1.5rem',
                        background: '#ffffff',
                        borderBottom: '1px solid #e5e5e5'
                      }}>
                        <img
                          src="/gallo-red-logo.png"
                          alt="Gallo"
                          style={{ height: '100px', marginBottom: '0', display: 'block', margin: '0 auto' }}
                        />
                      </div>

                      {/* Body */}
                      <div className="survey-card-body" style={{ padding: '1.5rem' }}>
                        <div className="survey-info">
                          <div className="info-item" style={{ justifyContent: 'flex-start', gap: '0.5rem' }}>
                            <span className="info-label">Survey:</span>
                            <span className="info-value" style={{ fontWeight: '600' }}>{mcSurvey.cycle_name}</span>
                          </div>

                          <div className="info-item" style={{ justifyContent: 'flex-start', gap: '0.5rem' }}>
                            <span className="info-label">Type:</span>
                            <span className="info-value">Meaningful Conversations</span>
                          </div>

                          <div className="info-item" style={{ justifyContent: 'flex-start', gap: '0.5rem' }}>
                            <span className="info-label">Due Date:</span>
                            <span className="info-value">
                              {endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          </div>

                          {isActionable && !isExpired && (
                            <div className="info-item" style={{ marginTop: '0.75rem' }}>
                              <span className={`days-remaining ${diffDays < 3 ? 'overdue' : ''}`} style={{
                                fontSize: '0.9375rem',
                                fontWeight: '600'
                              }}>
                                ⏰ {daysText}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="survey-card-footer" style={{ padding: '1rem 1.5rem 1.5rem' }}>
                        <button
                          onClick={() => {
                            if (isActionable && !isExpired) {
                              navigate(`/conversations/survey/${mcSurvey.access_token}`);
                            }
                          }}
                          disabled={!isActionable || isExpired}
                          className={`questionnaire-button ${isActionable && !isExpired ? 'questionnaire-button-primary' : ''}`}
                          style={{
                            width: '100%',
                            fontSize: '1.125rem',
                            fontWeight: '600',
                            textAlign: 'center',
                            padding: '0.875rem 1.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {getMCButtonText()}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Back to Apps - Only show for admins */}
          {isAnyAdmin && (
            <div className="back-to-home">
              <a href="/apps" className="questionnaire-button">
                View All Apps
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MySurveys;
